import React, { createContext, useContext, useState, useEffect } from 'react';

const SystemAdminContext = createContext();

export const useSystemAdmin = () => {
  const context = useContext(SystemAdminContext);
  if (!context) {
    throw new Error('useSystemAdmin must be used within a SystemAdminProvider');
  }
  return context;
};

export const SystemAdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [token, setToken] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Fetch CSRF token when authenticated (required for PUT/POST/DELETE to /api/admin/*)
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchCsrfToken = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/api/csrf-token`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.csrf_token) setCsrfToken(data.csrf_token);
        }
      } catch (e) {
        console.error('Failed to fetch CSRF token:', e);
      }
    };
    fetchCsrfToken();
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      const storedToken = localStorage.getItem('system_admin_token');
      const storedAdminInfo = localStorage.getItem('system_admin_info');

      if (storedToken && storedAdminInfo) {
        // Verify token is still valid
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/api/system-auth/verify-token`, {
          headers: {
            'Authorization': `Bearer ${storedToken}`,
          },
        });

        if (response.ok) {
          const verifyData = await response.json();
          // Merge stored admin info with verified data (which includes role)
          const storedInfo = JSON.parse(storedAdminInfo);
          const mergedInfo = { ...storedInfo, ...verifyData.admin_info };
          localStorage.setItem('system_admin_info', JSON.stringify(mergedInfo));
          setToken(storedToken);
          setAdminInfo(mergedInfo);
          setIsAuthenticated(true);
        } else {
          // Token is invalid, clear storage
          clearAuth();
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/system-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store authentication data
        localStorage.setItem('system_admin_token', data.access_token);
        localStorage.setItem('system_admin_info', JSON.stringify(data.admin_info));
        
        setToken(data.access_token);
        setAdminInfo(data.admin_info);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        await fetch(`${API_BASE_URL}/api/system-auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      clearAuth();
    }
  };

  const clearAuth = () => {
    localStorage.removeItem('system_admin_token');
    localStorage.removeItem('system_admin_info');
    setToken(null);
    setAdminInfo(null);
    setIsAuthenticated(false);
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (csrfToken) headers['x-csrf-token'] = csrfToken;
    return headers;
  };

  const value = {
    isAuthenticated,
    isLoading,
    adminInfo,
    token,
    login,
    logout,
    getAuthHeaders,
    checkAuthStatus,
  };

  return (
    <SystemAdminContext.Provider value={value}>
      {children}
    </SystemAdminContext.Provider>
  );
};
